import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Hummingbird from "./brand/Hummingbird";
import SocialIcons from "./SocialIcons";
import { ChevronDown } from "lucide-react";

const G = { gold: "#D4AF37", violet: "#7C4FBA" };

const Footer = () => {
  const { t } = useLang();
  const [openInit, setOpenInit] = useState(false);

  return (
    <footer className="relative mt-32 border-t border-white/5 bg-[#040814]" data-testid="site-footer">
      <div className="container-sat py-16">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Hummingbird size={42} />
              <div>
                <div className="serif text-2xl font-light">SAT</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                  {t("footer.tagline") || "Schweizer Akademie für Transformation"}
                </div>
              </div>
            </div>
            <p className="serif italic text-lg text-white/70 mt-6 leading-snug max-w-md">
              Du bist nicht falsch. Du bist verschlüsselt.
            </p>
            <p className="text-white/50 text-sm mt-3 max-w-md leading-relaxed">
              SAT begleitet Menschen dabei, ihren Human Transformation Code
              zu erkennen und aus alten Mustern eine bewusst gelebte Berufung zu formen.
            </p>
            <div className="mt-6">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-3">Folge der SAT Bewegung</div>
              <SocialIcons variant="footer" />
            </div>
          </div>

          {/* Angebote */}
          <div>
            <div className="overline mb-4">Angebote</div>
            <ul className="space-y-2 text-white/70 text-sm">
              <li>
                <Link to="/sat-master" className="hover:text-white transition" style={{ color: G.gold }}>
                  SAT Master
                </Link>
                <div className="text-xs text-white/40 mt-0.5">Eigene Transformation</div>
              </li>
              <li className="pt-2">
                <Link to="/mentor" className="hover:text-white transition" style={{ color: G.gold }}>
                  SAT Mentor
                </Link>
                <div className="text-xs text-white/40 mt-0.5">Ausbildung zum Begleiter</div>
              </li>
              <li className="pt-2">
                <Link to="/herzbewegung" className="hover:text-white transition" style={{ color: G.gold }}>
                  Herzbewegung
                </Link>
                <div className="text-xs text-white/40 mt-0.5">Liebe wird praktisch</div>
              </li>
            </ul>
          </div>

          {/* Methode */}
          <div>
            <div className="overline mb-4">Methode</div>
            <ul className="space-y-2 text-white/70 text-sm">
              <li>
                <Link to="/methoden/human-transformation-code" className="hover:text-white transition" style={{ color: G.gold }}>
                  SAT-HTC™
                </Link>
                <div className="text-xs text-white/40 mt-0.5">Human Transformation Code</div>
              </li>
              <li className="pt-2">
                <Link to="/methoden/human-potential-analysis" className="hover:text-white transition text-white/70">
                  SAT-HPA
                </Link>
                <div className="text-xs text-white/40 mt-0.5">Human Potential Analysis</div>
              </li>
              <li className="pt-2">
                <Link to="/los" className="hover:text-white transition text-white/70">
                  SAT Deep Dive
                </Link>
                <div className="text-xs text-white/40 mt-0.5">Life OS · Deep Scan</div>
              </li>
            </ul>
          </div>

          {/* SAT & Kontakt */}
          <div>
            <div className="overline mb-4">SAT Akademie</div>
            <ul className="space-y-2 text-white/65 text-sm">
              <li><Link to="/nenad-vladovic" className="hover:text-white">Nenad Vladović</Link></li>
              <li><Link to="/about" className="hover:text-white">Über SAT</Link></li>
              <li><Link to="/erstgespraech" className="hover:text-white">Erstgespräch anfragen</Link></li>
              <li><Link to="/gesellschaftsbarometer" className="hover:text-white" data-testid="footer-barometer-link">Gesellschaftsbarometer</Link></li>
              <li><a href="mailto:nv@sat-akademie.com" className="hover:text-white">nv@sat-akademie.com</a></li>
              <li className="pt-2 text-[10px] tracking-[0.28em] uppercase text-white/40">Rechtliches</li>
              <li><Link to="/impressum" className="hover:text-white text-xs">Impressum</Link></li>
              <li><Link to="/datenschutz" className="hover:text-white text-xs">Datenschutz</Link></li>
              <li><Link to="/agb" className="hover:text-white text-xs">AGB</Link></li>
            </ul>
          </div>
        </div>

        {/* Weitere Initiativen — kollabierbar, damit die Seite fokussiert bleibt */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <button
            onClick={() => setOpenInit(!openInit)}
            className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-white/50 hover:text-white transition"
            data-testid="footer-init-toggle"
          >
            Weitere Initiativen & Stimmen
            <ChevronDown size={14} className={`transition-transform ${openInit ? "rotate-180" : ""}`} />
          </button>
          {openInit && (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm" data-testid="footer-init-panel">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-3">Stimmen-Familie</div>
                <ul className="space-y-1.5 text-white/60">
                  <li><Link to="/vaterstimme/deck" className="hover:text-white">Vaterstimme</Link></li>
                  <li><Link to="/mutterstimme/deck" className="hover:text-white">Mutterstimme</Link></li>
                  <li><Link to="/maennerstimme/deck" className="hover:text-white">Männerstimme</Link></li>
                  <li><Link to="/frauenstimme/deck" className="hover:text-white">Frauenstimme</Link></li>
                  <li><Link to="/kinderstimme/deck" className="hover:text-white">Kinderstimme</Link></li>
                  <li><Link to="/elternstimme/deck" className="hover:text-white">Elternstimme</Link></li>
                  <li className="pt-1"><Link to="/akademie/decks" className="hover:text-white text-xs" style={{ color: G.gold }}>Alle Stimmen →</Link></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-3">SAT Ecosystem</div>
                <ul className="space-y-1.5 text-white/60">
                  <li><Link to="/ecosystem" className="hover:text-white">SAT Ecosystem</Link></li>
                  <li><Link to="/ecosystem/satcorporate" className="hover:text-white">SAT Corporate</Link></li>
                  <li><Link to="/ecosystem/satunity" className="hover:text-white">SATUNITY</Link></li>
                  <li><Link to="/partner-projekte" className="hover:text-white">Partner-Projekte</Link></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-3">Community</div>
                <ul className="space-y-1.5 text-white/60">
                  <li><Link to="/herzbewegung/deck" className="hover:text-white">Herzbewegung Deck</Link></li>
                  <li><Link to="/herzbewegung/herzbotschafter" className="hover:text-white">Herzbotschafter</Link></li>
                  <li><Link to="/forum" className="hover:text-white">Forum</Link></li>
                  <li><Link to="/events" className="hover:text-white">Events</Link></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-3">Ressourcen</div>
                <ul className="space-y-1.5 text-white/60">
                  <li><Link to="/quiz" className="hover:text-white">Quiz</Link></li>
                  <li><Link to="/30-spielregeln" className="hover:text-white">30 Spielregeln</Link></li>
                  <li><Link to="/podcast" className="hover:text-white">Podcast</Link></li>
                  <li><Link to="/tv" className="hover:text-white">SAT-TV</Link></li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} SAT — Schweizer Akademie für Transformation 🇨🇭</div>
          <div className="serif italic" style={{ color: G.gold }}>&bdquo;Love is Transformation.&ldquo;</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
